import * as v from "valibot"

import { detectBrowser } from "./detect_browser"
import type { DetectedBrowser } from "./detect_browser"
import { isGoogleVignetteShowing } from "./google_vignette"
import { trackAnalyticsEvent } from "./track_analytics_event"

type PromoPopup = {
  name: string
  maxImpressions: number
  // Absent: every browser sees the popup.
  allowedBrowsers?: DetectedBrowser[]
}

const PROMO_POPUPS: PromoPopup[] = [
  { name: "socials", maxImpressions: 2 },
  { name: "bromon", maxImpressions: 2, allowedBrowsers: ["chrome", "edge"] },
  { name: "coffee", maxImpressions: 2 },
  { name: "wallpapers", maxImpressions: 2 },
  { name: "community", maxImpressions: 2 },
]

const FIRST_SHOW_AT_ACTIVITY = 2
const ACTIVITY_BETWEEN_SHOWS = 3
const MS_BETWEEN_SHOWS = 60_000
const STORAGE_KEY = "sweezy_promo_popups"

const ManagerStateSchema = v.object({
  activityCount: v.number(),
  impressions: v.array(
    v.object({
      popup: v.string(),
      at: v.number(),
      activityCount: v.number(),
    }),
  ),
  converted: v.array(v.string()),
})
type ManagerState = v.InferOutput<typeof ManagerStateSchema>

/*
 * Opens a promo popup once the visitor has done enough activity (download clicks, successful
 *  add-to-extension) and remembers impressions and conversions across visits.
 * */
export class PromoPopupManager {
  static recordActivity() {
    const now = Date.now()
    const state = this.#loadState()
    state.activityCount += 1

    // Held back rather than spent: the popup could not open over the vignette anyway.
    const popup =
      this.#isDue(state, now) && !isGoogleVignetteShowing() ? this.#pickPromo(state) : null
    if (popup) {
      state.impressions.push({ popup: popup.name, at: now, activityCount: state.activityCount })
    }
    this.#saveState(state)

    if (!popup) return
    trackAnalyticsEvent("promo_popup_view", { popup_name: popup.name })
    window.dispatchEvent(new CustomEvent("popup:show", { detail: { name: popup.name } }))
  }

  static markConverted(name: string, link: string) {
    const state = this.#loadState()
    if (!state.converted.includes(name)) state.converted.push(name)
    this.#saveState(state)
    trackAnalyticsEvent("promo_popup_click", { popup_name: name, link })
  }

  static #isDue(state: ManagerState, now: number) {
    const last = state.impressions.at(-1)
    if (!last) return state.activityCount >= FIRST_SHOW_AT_ACTIVITY

    return (
      state.activityCount - last.activityCount >= ACTIVITY_BETWEEN_SHOWS &&
      now - last.at >= MS_BETWEEN_SHOWS
    )
  }

  static #eligiblePromos(state: ManagerState) {
    const browser = detectBrowser()

    return PROMO_POPUPS.filter((popup) => {
      if (state.converted.includes(popup.name)) return false

      const shown = state.impressions.filter((impression) => impression.popup === popup.name)
      if (shown.length >= popup.maxImpressions) return false

      return !popup.allowedBrowsers || (browser !== null && popup.allowedBrowsers.includes(browser))
    })
  }

  // Shown longest ago wins, never-shown before everything; ties break randomly.
  static #pickPromo(state: ManagerState): PromoPopup | null {
    const eligible = this.#eligiblePromos(state)
    const shownAt = (popup: PromoPopup) =>
      state.impressions.findLast((impression) => impression.popup === popup.name)?.at ?? -Infinity
    const oldest = Math.min(...eligible.map(shownAt))
    const candidates = eligible.filter((popup) => shownAt(popup) === oldest)

    return candidates[Math.floor(Math.random() * candidates.length)] ?? null
  }

  // Storage can be unavailable (Safari private mode, blocked cookies) or hold a record
  // this code never wrote; both start from an empty state, which the next save replaces.
  static #loadState(): ManagerState {
    const empty: ManagerState = { activityCount: 0, impressions: [], converted: [] }
    try {
      const parsed = v.safeParse(
        ManagerStateSchema,
        JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null"),
      )
      return parsed.success ? parsed.output : empty
    } catch {
      return empty
    }
  }

  static #saveState(state: ManagerState) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // no-op: see #loadState
    }
  }
}
